import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Send,
  Image as ImageIcon,
  X,
  Loader2,
  RefreshCw,
  Maximize2,
  MessageCircle
} from "lucide-react";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { getAuthHeaders, hasToken, BACKEND_URL } from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventChat({ user, eventId: propEventId, guestName: propGuestName, shareCode: propShareCode }) {
  const { eventId: paramEventId, shareCode: paramShareCode } = useParams();
  const eventId = propEventId || paramEventId;
  const shareCode = propShareCode || paramShareCode || eventId; // Fallback to eventId if no shareCode
  const guestName = propGuestName || (user?.display_name || user?.name || "Gast");
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [event, setEvent] = useState(null);
  const [connected, setConnected] = useState(false);
  const [chatMode] = useState("group");
  const [selectedRecipient] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const wsRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom - immer beim Laden und bei neuen Nachrichten
  const scrollToBottom = useCallback((behavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Bei neuen Nachrichten nach unten scrollen
  useEffect(() => {
    if (messages.length > 0) {
      // Bei initialem Laden sofort, sonst smooth
      scrollToBottom(isInitialLoad ? "auto" : "smooth");
      if (isInitialLoad) setIsInitialLoad(false);
    }
  }, [messages, scrollToBottom, isInitialLoad]);

  const fetchEvent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`, authConfig());
      setEvent(response.data);
    } catch (error) {
      console.error("Failed to fetch event:", error);
      toast.error("Event nicht gefunden");
      navigate("/dashboard");
    }
  }, [eventId, navigate]);

  const fetchMessages = useCallback(async () => {
    try {
      let url;
      if (user) {
        url = selectedRecipient
          ? `${API}/events/${eventId}/chat?recipient_id=${selectedRecipient}`
          : `${API}/events/${eventId}/chat`;
      } else {
        url = `${API}/public/event/${shareCode}/chat`;
      }
      const response = await axios.get(url, user ? authConfig() : {});
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, [eventId, selectedRecipient]);

  const connectWebSocket = useCallback(() => {
    if (!user || !eventId) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL?.replace('https://', '').replace('http://', '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${eventId}?user_id=${user.id}&user_name=${encodeURIComponent(user.name)}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setConnected(true);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "message") {
            const msg = data.message;
            if (!msg.recipient_id || msg.sender_id === user.id || msg.recipient_id === user.id) {
              setMessages(prev => {
                if (prev.find(m => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
            }
          }
        } catch (e) {
          console.error("Error parsing message:", e);
        }
      };

      wsRef.current.onclose = (event) => {
        setConnected(false);
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };

      wsRef.current.onerror = () => {
        setConnected(false);
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setConnected(false);
    }
  }, [eventId, user]);

  useEffect(() => {
    fetchEvent();
    fetchMessages();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, "Component unmounting");
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [fetchEvent, fetchMessages, connectWebSocket]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Bitte wähle eine Bilddatei aus");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild ist zu groß (max. 10MB)");
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target?.result);
    reader.readAsDataURL(file);
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Upload image to event media
  const uploadImage = async () => {
    if (!selectedImage) return null;

    const formData = new FormData();
    formData.append('file', selectedImage);

    try {
      const response = await axios.post(
        `${API}/events/${eventId}/chat/upload`,
        formData,
        {
          ...authConfig(),
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        }
      );
      return response.data.url;
    } catch (error) {
      // Fallback to media upload if chat upload endpoint doesn't exist
      try {
        const response = await axios.post(
          `${API}/events/${eventId}/media/upload`,
          formData,
          {
            ...authConfig(),
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
          }
        );
        return response.data.url;
      } catch (fallbackError) {
        console.error("Failed to upload image:", fallbackError);
        throw fallbackError;
      }
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;

    setUploading(true);
    let imageUrl = null;

    try {
      // Upload image first if selected
      if (selectedImage) {
        imageUrl = await uploadImage();
      }

      const messageContent = newMessage.trim();
      const messageType = imageUrl ? "image" : "text";
      const content = imageUrl ? (messageContent ? `${messageContent}\n[Bild: ${imageUrl}]` : `[Bild: ${imageUrl}]`) : messageContent;

      // Try WebSocket first
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const messageData = {
          content: content,
          recipient_id: chatMode === "private" ? selectedRecipient : null,
          message_type: messageType,
          image_url: imageUrl
        };
        wsRef.current.send(JSON.stringify(messageData));
        setNewMessage("");
        clearSelectedImage();
      } else {
        // Fallback to HTTP API
        if (user) {
          const response = await axios.post(
            `${API}/events/${eventId}/chat`,
            {
              content: content,
              recipient_id: chatMode === "private" ? selectedRecipient : null,
              message_type: messageType,
              image_url: imageUrl
            },
            authConfig()
          );
          setMessages(prev => [...prev, response.data]);
        } else {
          // Public Chat Post
          const response = await axios.post(
            `${API}/public/event/${shareCode}/chat`,
            {
              content: content,
              message_type: messageType,
              image_url: imageUrl,
              guest_name: guestName
            }
          );
          setMessages(prev => [...prev, response.data]);
        }
        setNewMessage("");
        clearSelectedImage();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Nachricht konnte nicht gesendet werden");
    } finally {
      setUploading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Extract image URL from message content
  const extractImageUrl = (content) => {
    const match = content?.match(/\[Bild: (.*?)\]/);
    return match ? match[1] : null;
  };

  // Get text without image tag
  const getTextContent = (content) => {
    return content?.replace(/\[Bild: .*?\]/g, '').trim() || '';
  };

  // Format date header
  const formatDateHeader = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Heute";
    if (isYesterday(date)) return "Gestern";
    return format(date, "EEEE, d. MMMM", { locale: de });
  };

  // Group messages by date
  const getDateFromMessage = (msg) => {
    if (!msg.created_at) return null;
    return format(parseISO(msg.created_at), "yyyy-MM-dd");
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Fixed Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/event/${eventId}`)}
                className="rounded-full hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-gray-900 truncate max-w-[200px] sm:max-w-none">
                  {event?.title || "Chat"}
                </h1>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500">
                    {chatMode === "group" ? "Gruppenchat" : "Privater Chat"}
                  </span>
                  {connected ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      Online
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-600">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>
                      Verbinde...
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchMessages}
              className="rounded-full hover:bg-gray-100"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Scrollable Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto"
      >
        <div className="max-w-3xl mx-auto px-4 py-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[50vh] text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10 text-indigo-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Nachrichten</h3>
              <p className="text-gray-500 max-w-xs">Starte die Unterhaltung mit deinen Gästen!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, index) => {
                const isOwn = msg.sender_id === user?.id;
                const showDate = index === 0 || getDateFromMessage(msg) !== getDateFromMessage(messages[index - 1]);
                const showAvatar = index === 0 || messages[index - 1]?.sender_id !== msg.sender_id;
                const imageUrl = extractImageUrl(msg.content) || msg.image_url;
                const textContent = getTextContent(msg.content);

                return (
                  <div key={msg.id || index}>
                    {/* Date Separator */}
                    {showDate && msg.created_at && (
                      <div className="flex justify-center my-4">
                        <span className="bg-gray-200/80 text-gray-600 text-xs font-medium px-3 py-1 rounded-full">
                          {formatDateHeader(msg.created_at)}
                        </span>
                      </div>
                    )}

                    {/* Message Bubble */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                      className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`flex gap-2 max-w-[80%] ${isOwn ? "flex-row-reverse" : "flex-row"}`}>
                        {/* Avatar */}
                        {!isOwn && (
                          showAvatar ? (
                            <div className="flex-shrink-0 self-end">
                              {msg.sender_picture ? (
                                <img
                                  src={msg.sender_picture}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                                  <span className="text-xs font-bold text-white">
                                    {msg.sender_name?.[0]?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-8" />
                          )
                        )}

                        {/* Message Content */}
                        <div className="space-y-1">
                          {/* Sender Name */}
                          {showAvatar && !isOwn && (
                            <p className="text-xs font-medium text-gray-500 ml-1">
                              {msg.sender_name}
                            </p>
                          )}

                          {/* Message Bubble */}
                          <div
                            className={`rounded-2xl overflow-hidden ${isOwn
                              ? "bg-indigo-600 text-white"
                              : "bg-white border border-gray-200"
                              }`}
                          >
                            {/* Image */}
                            {imageUrl && (
                              <div
                                className="relative cursor-pointer group"
                                onClick={() => setFullscreenImage(imageUrl)}
                              >
                                <img
                                  src={imageUrl.startsWith('/') ? `${BACKEND_URL}${imageUrl}` : imageUrl}
                                  alt=""
                                  className="w-full max-w-[280px] object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                  <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </div>
                            )}

                            {/* Text */}
                            {textContent && (
                              <div className="px-4 py-2">
                                <p className={`text-sm whitespace-pre-wrap ${isOwn ? 'text-white' : 'text-gray-800'}`}>
                                  {textContent}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Time */}
                          <p className={`text-[10px] text-gray-400 ${isOwn ? "text-right mr-1" : "ml-1"}`}>
                            {msg.created_at && format(parseISO(msg.created_at), "HH:mm")}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} className="h-1" />
            </div>
          )}
        </div>
      </div>

      {/* Image Preview */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex-shrink-0 bg-gray-100 border-t border-gray-200 px-4 py-2"
          >
            <div className="max-w-3xl mx-auto flex items-center gap-3">
              <div className="relative">
                <img
                  src={previewImage}
                  alt="Vorschau"
                  className="h-14 w-14 object-cover rounded-lg border border-gray-300"
                />
                <button
                  onClick={clearSelectedImage}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-sm"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <span className="text-sm text-gray-600 truncate">
                {selectedImage?.name}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed Input Area */}
      <div className="flex-shrink-0 bg-white border-t border-gray-200 p-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-2 items-center">
            {/* Image Upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="rounded-full hover:bg-gray-100 flex-shrink-0 h-10 w-10"
            >
              <ImageIcon className="w-5 h-5 text-gray-500" />
            </Button>

            {/* Text Input */}
            <Input
              data-testid="chat-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nachricht schreiben..."
              disabled={uploading}
              className="flex-1 rounded-full py-5 bg-gray-100 border-0 focus:bg-white focus:ring-2 focus:ring-indigo-200"
            />

            {/* Send Button */}
            <Button
              data-testid="send-btn"
              onClick={sendMessage}
              disabled={(!newMessage.trim() && !selectedImage) || uploading}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 flex-shrink-0 h-10 w-10"
              size="icon"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setFullscreenImage(null)}
          >
            <button
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center"
              onClick={() => setFullscreenImage(null)}
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={fullscreenImage.startsWith('/') ? `${BACKEND_URL}${fullscreenImage}` : fullscreenImage}
              alt=""
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
